import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { Add20Filled, Delete20Regular } from '@fluentui/react-icons';
import {
  UserKind,
  UserListSorting,
  getSortedUserLabel,
  sortUsersByPreference,
} from '@mnspro-cloud/services';
import { Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarUser } from '../Avatar';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { List, ListEntryProps } from '../List';
import { PersonPicker } from '../PersonPicker';
import { Text } from '../Text';

export interface PersonListProps<T extends Person = Person> {
  title: string;
  persons: T[];
  selectedPersons: T[];
  onAddPersonClicked(): void;
  onUpdate(persons: Person[]): void;
  addButtonContent?: React.ReactNode;
  isLoading?: boolean;
  isLoadingPersons?: boolean;
  className?: string;
  getPersonListEntry?: (person: T) => ListEntryProps;
  sortMode?: UserListSorting;
}

export const PersonList = <T extends Person = Person>({
  title,
  persons,
  selectedPersons,
  onAddPersonClicked,
  onUpdate,
  addButtonContent,
  isLoading,
  isLoadingPersons,
  className,
  getPersonListEntry,
  sortMode = UserListSorting.FirstName,
}: PersonListProps<T>) => {
  const { t } = useTranslation();
  const [showPersonPicker, setShowPersonPicker] = useState(false);

  const sorted = useMemo(
    () => sortUsersByPreference(selectedPersons, sortMode, (person) => person as UserKind),
    [selectedPersons, sortMode],
  );
  const selectedIds = useMemo(() => new Set(selectedPersons.map((s) => s.id)), [selectedPersons]);

  const entries: ListEntryProps[] = useMemo(() => {
    if (getPersonListEntry) {
      return sorted.map(getPersonListEntry);
    }

    return sorted.map((person) => {
      const label = getSortedUserLabel(person as UserKind, sortMode);
      return {
        label,
        avatar: <LazyAvatarUser name={label} userId={person.id} />,
        content: person.mailAddress,
        actions: [
          {
            'data-testid': 'removePersonButton',
            icon: <Delete20Regular />,
            onClick: () =>
              onUpdate(selectedPersons.filter((selected) => selected.id !== person.id)),
          },
        ],
      };
    });
  }, [sorted, sortMode]);

  return (
    <Flex direction="column" className={className}>
      <PersonPicker
        sortMode={sortMode}
        isLoading={isLoadingPersons}
        persons={persons.filter((p) => !selectedIds.has(p.id))}
        onCancelClick={() => setShowPersonPicker(false)}
        onPersonSelected={(persons: Person[]) => {
          const personToAdd = persons[0];
          onUpdate([...selectedPersons, personToAdd]);
          setShowPersonPicker(false);
        }}
        open={showPersonPicker}
      />
      {isLoading && (
        <Skeleton data-testid="titleSkeleton">
          <SkeletonItem size={20} style={{ width: '90px' }} />
        </Skeleton>
      )}
      {!isLoading && (
        <Text weight="bold" data-testid="title">
          {title}
        </Text>
      )}
      {isLoading && (
        <Skeleton data-testid="addButtonSkeleton">
          <SkeletonItem size={32} style={{ width: '280px' }} />
        </Skeleton>
      )}
      {!isLoading && (
        <Button
          data-testid="addPersonButton"
          icon={<Add20Filled />}
          appearance="primary"
          onClick={() => {
            onAddPersonClicked();
            setShowPersonPicker(true);
          }}
        >
          {addButtonContent ?? t('add', { defaultValue: 'Hinzufügen' })}
        </Button>
      )}
      {isLoading && (
        <Skeleton data-testid="personListSkeleton">
          <SkeletonItem size={40} style={{ width: '100%' }} />
        </Skeleton>
      )}
      {!isLoading && <List data-testid="personList" entries={entries} />}
    </Flex>
  );
};
